import React, { useMemo, useState } from 'react';
import { Button, Drawer, Flex, Form, Input, Select, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { UploadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import useForm from '../../hooks/utility/useForm';
import { PolygraphyProductFormat, PolygraphyProductType } from '../../services/exports/Constants';
import useScreenType from '../../hooks/context/useScreenType';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import useApiClient from '../../hooks/api/useApiClient';
import { Utils } from '../../services/helpers/Utils';

const PolygraphyProductFormatMimes = {
  [PolygraphyProductFormat.FlyerA5]: 'application/pdf',
  [PolygraphyProductFormat.FlyerA6]: 'application/pdf',
  [PolygraphyProductFormat.DoorStickerA5]: 'application/pdf',
  [PolygraphyProductFormat.Sticker60x60]: 'image/png,image/jpeg',
};

export default function UploadPolygraphyProductModal({ product, onSuccess, onClose }) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Modals:Marketing:UploadPolygraphyProductModal' });

  const { PolygraphyManager } = useApiClient();

  const { isDesktop } = useScreenType();
  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel();

  const [loading, setLoading] = useState(false);
  const { data, update } = useForm({
    name: product?.name ?? '',
    format: product?.format ?? PolygraphyProductFormat.FlyerA5,
    file: product?.file_url
      ? {
          uid: 'file',
          status: 'done',
          name: collect(product.file_url.split('/')).last(),
          url: product.file_url,
        }
      : null,
  });

  const onSave = async () => {
    const _data = {
      ...data,
      file: data.file instanceof File ? data.file : data.file?.url,
    };

    resetFeedback();
    setLoading(true);
    const { success, data: responseData } = product
      ? await PolygraphyManager.updateProduct(product.id, Utils.createFormData({ data: _data, _method: 'PUT' }))
      : await PolygraphyManager.createProduct(PolygraphyProductType.Custom, Utils.createFormData(_data));
    setLoading(false);

    if (!success) {
      return setErrorFeedback(responseData);
    }

    toast.success(t('toasts.changes_got_saved'));
    onSuccess(responseData);

    return onClose();
  };

  const formatOptions = useMemo(
    () =>
      Object.values(PolygraphyProductFormat).map((item) => ({
        label: t(`form.format.options.${item}`, { defaultValue: item }),
        value: item,
      })),
    [i18n.language],
  );

  return (
    <Drawer open onClose={onClose} size={window.innerWidth * (isDesktop ? 0.4 : 1)}>
      {renderFeedbackLabel}
      <Form layout="vertical">
        <Form.Item label={t('form.name.label')} required>
          <Input
            value={data.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder={t('form.name.placeholder')}
          />
        </Form.Item>
        <Form.Item label={t('form.format.label')} required>
          <Select
            value={data.format}
            options={formatOptions}
            onChange={(value) => update('format', value)}
            placeholder={t('form.format.placeholder')}
          />
        </Form.Item>
        <Form.Item label={t('form.file.label')} required>
          <Upload
            multiple={false}
            maxCount={1}
            fileList={data.file ? [data.file] : []}
            listType="picture"
            accept={PolygraphyProductFormatMimes[data.format]}
            beforeUpload={(file) => {
              update('file', file);

              return false;
            }}
            onRemove={() => update('file', null)}
          >
            <Button icon={<UploadOutlined />}>{t('buttons.upload')}</Button>
          </Upload>
        </Form.Item>
      </Form>
      <Flex justify="end">
        <Button type="primary" loading={loading} onClick={onSave}>
          {t('buttons.save')}
        </Button>
      </Flex>
    </Drawer>
  );
}
